/* eslint-disable react/jsx-no-duplicate-props */
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { Slide, ButtonGroup, IconButton, Stack, Typography, TextField, InputAdornment, Zoom } from '@mui/material';
import { calculatePathLengths } from '../utils';

const FlowsItem = ({ flow, setOpen }: { flow: any; setOpen: Dispatch<SetStateAction<boolean>> }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { longestPathLength, shortestPathLength } = useMemo(() => calculatePathLengths(flow.steps, flow.edges), [flow]);
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      width="100%"
      height="71px"
      p="14px"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => {
        setOpen(false);
        flow?.handleFlowSelect(flow);
      }}
      sx={{
        background: isHovered ? 'rgba(255, 255, 255, 0.10)' : 'transparent',
        cursor: 'pointer',
      }}
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: '37px',
          height: '37px',
          borderRadius: '8px',
          background: '#FFF',
          '& img': {
            width: 'inherit',
            height: 'inherit',
            borderRadius: '8px',
          },
        }}
      >
        <img
          src={flow?.flowSetting?.thumbnail ? `${process.env.REACT_APP_AWS}/tours/${flow?.tourId}/experience/flows/icons/${flow?.flowSetting?.thumbnail}` : '/flow.png'}
          alt="flow-thumbnail"
        />
      </Stack>
      <Stack width="100%">
        <Typography fontSize="16px" lineHeight="21px" fontWeight={500}>
          {flow.name}
        </Typography>
        <Typography fontSize="14px" lineHeight="normal" fontWeight={400} color="rgba(255, 255, 255, 0.60)">
          {!flow.started ? 'Not started' : `${longestPathLength !== shortestPathLength ? `${shortestPathLength} - ${longestPathLength} steps` : `${longestPathLength} steps`}`}
        </Typography>
      </Stack>
      <Zoom in={isHovered}>
        <i className="fa-solid fa-arrow-right" style={{ color: '#fff' }} />
      </Zoom>
    </Stack>
  );
};

export const FlowsMenu = ({ flows, tourCustomButton }) => {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const filteredFlows = useMemo(
    () =>
      flows.filter((f) => f?.name?.toLowerCase()?.includes(searchText.toLowerCase()) || !!JSON.parse(f?.keywords)?.find((keyword) => keyword?.includes(searchText.toLowerCase()))),
    [flows, searchText],
  );

  return (
    <>
      <ButtonGroup
        orientation="vertical"
        sx={{
          position: 'absolute',
          top: tourCustomButton ? '145px' : '195px',
          right: 0,
          backdropFilter: ' blur(8.14285659790039px)',
          background: 'rgba(0, 0, 0, 0.80)',
          borderRadius: '10px 0px 0px 10px',
        }}
      >
        <IconButton onClick={() => setOpen((prev) => !prev)}>
          <i
            className="fa-solid fa-atom-simple"
            style={{
              color: '#FFF',
            }}
          />
        </IconButton>
      </ButtonGroup>
      <Slide in={open} direction="left">
        <Stack
          direction="column"
          alignItems="center"
          justifyContent="flex-start"
          flexWrap="wrap"
          sx={{
            position: 'fixed',
            top: '100px',
            right: '50px',
            width: '100%',
            maxWidth: '334px',
            height: 'auto',
            minHeight: 'max(30vh, 350px)',
            zIndex: 999,
            color: 'white',
            transition: 'all 225ms cubic-bezier(0, 0, 0.2, 1) 0ms !important',
            borderRadius: '12px',
            background: 'rgba(13, 13, 13, 0.75)',
            backdropFilter: 'blur(20px)',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            height="58px"
            p="10px 14px"
            sx={{
              background: '#0D0D0D',
              borderRadius: '12px 12px 0px 0px',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Stack
                sx={{
                  borderRadius: '8px',
                  background: 'rgba(255, 255, 255, 0.10)',
                }}
              >
                <i
                  className="fa-solid fa-atom-simple"
                  style={{
                    color: '#FFF',
                  }}
                />
              </Stack>
              <Typography lineHeight="24px" fontSize="16px" fontWeight={600}>
                Flows list
              </Typography>
            </Stack>
            <IconButton onClick={() => setOpen(false)}>
              <i
                className="fa fa-times"
                style={{
                  color: '#FFF',
                }}
              />
            </IconButton>
          </Stack>
          <Stack mt="10px" width="100%" p="0 14px">
            <TextField
              variant="standard"
              fullWidth
              placeholder="Search"
              sx={{
                background: '#0D0D0D',
                padding: '0px 10px',
                borderRadius: '8px',
              }}
              value={searchText}
              inputProps={{
                style: {
                  color: '#fff',
                  height: 45,
                },
              }}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                sx: { '&:before': { borderBottom: 'none !important' }, '&:after': { borderBottom: 'none !important' } },
                startAdornment: (
                  <InputAdornment position="start">
                    <i className="fa fa-search" style={{ color: 'lightgray' }} />
                  </InputAdornment>
                ),
                ...(searchText && {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        sx={{
                          color: 'lightgray',
                        }}
                        onClick={() => {
                          setSearchText('');
                        }}
                      >
                        <i className="fas fa-times" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }),
              }}
            />
          </Stack>
          <Stack mt="10px" width="100%">
            <Typography p="0 14px" lineHeight="24px" fontSize="16px" fontWeight={500}>
              Results
            </Typography>
            <Stack
              maxHeight="434px"
              sx={{
                overflowY: 'scroll',
              }}
            >
              {filteredFlows?.map((flow: any) => {
                return <FlowsItem flow={flow} key={flow?.id} setOpen={setOpen} />;
              })}
            </Stack>
          </Stack>
        </Stack>
      </Slide>
    </>
  );
};
